/**
 * Project API — Connect RPC implementation
 * Calls ProjectDataService via sabaody gateway.
 * Returns the same types as the REST projectApi.
 *
 * NOTE: Chat operations (listProjectChats, getProjectChat, createProjectChat, forkProjectChat)
 * stay on REST — they're handled by express-backend's MongoDB/shanks integration.
 * File uploads (createProject with files, uploadProjectFiles) also stay REST (FormData).
 */ import { projectDataClient } from '../connect/clients';
function timestampToString(ts) {
    if (!ts) return '';
    return new Date(Number(ts.seconds) * 1000).toISOString();
}
function projectTypeFromProto(t) {
    switch(t){
        case 2:
            return 'proposal';
        case 3:
            return 'contract';
        // 4 is PROJECT_TYPE_CAPTURE (Phase 1a) — no frontend type yet, falls
        // through to 'general'. 5 is PROJECT_TYPE_PRE_AWARD (Phase 4a).
        case 5:
            return 'pre_award';
        default:
            return 'general';
    }
}
function projectTypeToProto(t) {
    switch(t){
        case 'proposal':
            return 2;
        case 'contract':
            return 3;
        case 'pre_award':
            return 5;
        default:
            return 1;
    }
}
function toProjectStatus(s) {
    return s === 'archived' ? 'archived' : 'active';
}
function toMemberRole(r) {
    switch(r){
        case 'owner':
            return 'owner';
        case 'view_only':
            return 'view_only';
        default:
            return 'member';
    }
}
function toFileProcessingStatus(s) {
    switch(s){
        case 'queued':
        case 'processing':
        case 'ready':
        case 'error':
            return s;
        default:
            return 'none';
    }
}
function mapProject(p) {
    return {
        projectId: p.projectId,
        name: p.name,
        description: p.description || undefined,
        status: toProjectStatus(p.status),
        type: projectTypeFromProto(p.type),
        lastActivityAt: timestampToString(p.lastActivityAt),
        createdAt: timestampToString(p.createdAt)
    };
}
function mapMember(m) {
    return {
        userId: m.userId,
        email: m.email,
        name: m.name,
        role: toMemberRole(m.role)
    };
}
export const projectApiConnect = {
    async listProjects (params) {
        const response = await projectDataClient.listProjects({
            status: params?.status ?? 'all',
            page: params?.page ?? 1,
            limit: params?.limit ?? 20
        });
        return {
            projects: response.projects.map((p)=>({
                    projectId: p.projectId,
                    name: p.name,
                    description: p.description || undefined,
                    status: toProjectStatus(p.status),
                    type: projectTypeFromProto(p.type),
                    lastActivityAt: timestampToString(p.lastActivityAt),
                    createdAt: timestampToString(p.createdAt),
                    role: toMemberRole(p.callerRole),
                    memberCount: p.memberCount
                })),
            pagination: {
                total: response.total,
                page: response.page,
                limit: response.limit,
                totalPages: Math.ceil(response.total / (response.limit || 20))
            }
        };
    },
    async createProject (request) {
        // File uploads stay on REST — only JSON creates go through Connect
        if (request.files && request.files.length > 0) {
            throw new Error('File uploads must use REST API');
        }
        const response = await projectDataClient.createProject({
            name: request.name ?? '',
            description: request.description ?? '',
            type: projectTypeToProto(request.type),
            contractInfo: request.contractNumber || request.contractType ? {
                contractNumber: request.contractNumber ?? '',
                contractType: request.contractType ?? ''
            } : undefined
        });
        const project = response.project;
        return {
            project: {
                projectId: project?.projectId ?? '',
                name: project?.name ?? '',
                description: project?.description || undefined,
                status: 'active',
                type: projectTypeFromProto(project?.type ?? 0),
                lastActivityAt: timestampToString(project?.lastActivityAt),
                createdAt: timestampToString(project?.createdAt),
                role: 'owner',
                memberCount: response.members.length
            }
        };
    },
    async getProject (projectId) {
        const response = await projectDataClient.getProject({
            projectId
        });
        if (!response.project) {
            throw new Error('Project not found');
        }
        const members = response.members.map(mapMember);
        const project = mapProject(response.project);
        return {
            project,
            members,
            role: toMemberRole(response.callerRole),
            memberCount: members.length,
            solicitation: response.project.proposalInfo ? {
                solicitationNumber: response.project.proposalInfo.solicitationNumber,
                agency: response.project.proposalInfo.agency,
                dueDate: response.project.proposalInfo.dueDate ? timestampToString(response.project.proposalInfo.dueDate) : undefined,
                naicsCode: response.project.proposalInfo.naicsCode,
                setAside: response.project.proposalInfo.setAside,
                contractType: response.project.proposalInfo.contractType || undefined,
                contractVehicle: response.project.proposalInfo.contractVehicle || undefined,
                office: response.project.proposalInfo.office || undefined
            } : undefined,
            contract: response.project.contractInfo ? {
                contractNumber: response.project.contractInfo.contractNumber || undefined,
                contractType: response.project.contractInfo.contractType || undefined,
                agency: response.project.contractInfo.agency?.name || undefined,
                contractAnalysisId: response.project.contractInfo.contractAnalysisId || undefined,
                totalValue: response.project.contractInfo.totalValue ? {
                    baseValue: response.project.contractInfo.totalValue.baseValue,
                    totalCeiling: response.project.contractInfo.totalValue.totalCeiling,
                    currency: response.project.contractInfo.totalValue.currency
                } : undefined
            } : undefined
        };
    },
    async updateProject (projectId, request) {
        const response = await projectDataClient.updateProject({
            projectId,
            name: request.name ?? '',
            description: request.description ?? ''
        });
        if (!response.project) throw new Error('Project not found');
        return {
            project: {
                ...mapProject(response.project),
                updatedAt: timestampToString(response.project.updatedAt)
            }
        };
    },
    async archiveProject (projectId) {
        const response = await projectDataClient.archiveProject({
            projectId
        });
        if (!response.project) throw new Error('Project not found');
        return {
            project: mapProject(response.project)
        };
    },
    async unarchiveProject (projectId) {
        const response = await projectDataClient.unarchiveProject({
            projectId
        });
        if (!response.project) throw new Error('Project not found');
        return {
            project: mapProject(response.project)
        };
    },
    async deleteProject (projectId) {
        const response = await projectDataClient.deleteProject({
            projectId
        });
        return {
            projectId: response.projectId,
            deletedAt: response.deletedAt
        };
    },
    async addMembers (projectId, request) {
        await projectDataClient.addMembers({
            projectId,
            members: request.members.map((m)=>({
                    userId: m.userId,
                    email: m.email,
                    name: m.name,
                    role: m.role
                }))
        });
        return {
            projectId,
            added: request.members,
            skipped: []
        };
    },
    async removeMember (projectId, userId) {
        await projectDataClient.removeMember({
            projectId,
            userId
        });
        return {
            success: true,
            message: 'Member removed'
        };
    },
    async updateMemberRole (projectId, userId, request) {
        await projectDataClient.updateMemberRole({
            projectId,
            userId,
            role: request.role
        });
        return {
            success: true,
            message: 'Role updated'
        };
    },
    async transferOwnership (projectId, request) {
        const response = await projectDataClient.transferOwnership({
            projectId,
            newOwnerId: request.newOwnerId
        });
        return {
            success: true,
            message: 'Ownership transferred',
            data: {
                projectId,
                members: response.members.map(mapMember),
                lastActivityAt: new Date().toISOString()
            }
        };
    },
    async leaveProject (projectId) {
        await projectDataClient.leaveProject({
            projectId
        });
        return {
            success: true,
            message: 'Left project'
        };
    },
    /**
   * List a project's files.
   *
   * search/sortBy/sortOrder are applied here rather than sent, because
   * ListProjectFilesRequest carries only project_id and pagination — the proto
   * has no filter or sort fields. The REST client did this server-side, so
   * dropping the arguments (as this adapter previously did, hence `_params`)
   * silently broke the file grid's search box and sort control: the hook still
   * re-keyed its query, refetched, and got the same rows back.
   *
   * Pagination *is* a supported RPC field, but slicing a single RPC page
   * before filtering/sorting would paginate the wrong thing — page 2 of the
   * unsorted set isn't page 2 of the sorted one. So the RPC is asked for
   * pageSize: 5000 (its own ceiling — see parsePagination in
   * services/zou/pkg/connecthandlers/projectdata.go), same as
   * proposalProjectApi.connect.ts does for its own "operate client-side over
   * everything" calls, and params.page/params.limit slice the filtered,
   * sorted result afterward.
   */ async listProjectFiles (projectId, params) {
        const response = await projectDataClient.listProjectFiles({
            projectId,
            pagination: {
                pageSize: 5000
            }
        });
        // The REST API enriches files with member names — Connect returns raw data
        const mapped = response.files.map((f)=>({
                attachmentId: f.fileId,
                name: f.filename,
                contentType: f.mimeType,
                size: Number(f.fileSize),
                uploadedBy: {
                    userId: f.uploadedBy,
                    name: '',
                    email: ''
                },
                uploadedAt: timestampToString(f.uploadedAt),
                processingStatus: toFileProcessingStatus(f.status),
                documentType: f.documentType,
                fileCategory: f.fileCategory,
                subtype: f.subtype,
                amendmentNumber: f.amendmentNumber,
                associatedAmendment: f.associatedAmendment || undefined,
                sourceDocumentId: f.sourceDocumentId,
                amendmentSequence: f.amendmentSequence,
                pageCount: f.pageCount,
                classification: f.classification ? {
                    documentType: f.classification.documentType,
                    primaryType: f.classification.primaryType,
                    confidence: f.classification.confidence,
                    confidenceSource: f.classification.confidenceSource,
                    classifiedAt: f.classification.classifiedAt || '',
                    summary: f.classification.summary || null,
                    keyTopics: f.classification.keyTopics?.length ? f.classification.keyTopics : null,
                    keyEntities: f.classification.keyEntities?.length ? f.classification.keyEntities : null,
                    solicitationNumber: f.classification.solicitationNumber || null,
                    agency: f.classification.agency || null,
                    dueDate: f.classification.dueDate || null,
                    contractNumber: f.classification.contractNumber || null,
                    parentDocumentType: f.classification.parentDocumentType || null,
                    additionalSections: f.classification.additionalSections?.length ? f.classification.additionalSections : null
                } : undefined
            }));
        const search = params?.search?.trim().toLowerCase();
        const filtered = search ? mapped.filter((f)=>f.name.toLowerCase().includes(search)) : mapped;
        const sortBy = params?.sortBy;
        const sorted = sortBy ? [
            ...filtered
        ].sort((a, b)=>{
            const dir = params?.sortOrder === 'asc' ? 1 : -1;
            if (sortBy === 'name') return a.name.localeCompare(b.name) * dir;
            if (sortBy === 'size') return (a.size - b.size) * dir;
            // uploadedAt: ISO-8601 strings, so a lexicographic compare is
            // chronological and needs no Date parsing.
            return a.uploadedAt.localeCompare(b.uploadedAt) * dir;
        }) : filtered;
        const limit = params?.limit;
        const paged = limit ? sorted.slice(((params?.page ?? 1) - 1) * limit, ((params?.page ?? 1) - 1) * limit + limit) : sorted;
        return {
            files: paged,
            // The count is of the filtered set the caller can page through, not
            // just what's on this page — the file grid renders this as "N files"
            // beside the results.
            count: sorted.length
        };
    },
    async deleteProjectFile (projectId, attachmentId) {
        await projectDataClient.deleteProjectFile({
            projectId,
            fileId: attachmentId
        });
        return {
            success: true,
            attachmentId
        };
    },
    async convertToContract (projectId, request) {
        const response = await projectDataClient.createContractProjectFromProposal({
            proposalProjectId: projectId,
            name: request.name ?? '',
            description: request.description ?? '',
            businessUnitId: request.businessUnitId ?? '',
            archiveSourceProposal: request.archiveSourceProposal ?? false,
            // The dialog's optional contract number/type ride on ContractInfo.
            ...request.contractNumber || request.contractType ? {
                contractData: {
                    contractNumber: request.contractNumber ?? '',
                    contractType: request.contractType ?? ''
                }
            } : {}
        });
        if (!response.project) throw new Error('Conversion failed: project not returned');
        return {
            project: mapProject(response.project),
            conversionSummary: {
                requirementsCopied: response.conversionSummary?.requirementsCopied ?? 0,
                attachmentsCopied: response.conversionSummary?.attachmentsCopied ?? 0,
                clinsCopied: response.conversionSummary?.clinsCopied ?? 0,
                deliverablesCopied: response.conversionSummary?.deliverablesCopied ?? 0,
                warnings: []
            }
        };
    },
    // Project Chats
    async listProjectChats (projectId, params) {
        const response = await projectDataClient.listProjectChats({
            projectId,
            sort: params?.sort ?? 'createdAt',
            order: params?.order ?? 'desc',
            search: params?.search ?? ''
        });
        return {
            chats: (response.chats ?? []).map((c)=>({
                    chatId: c.chatId,
                    title: c.title,
                    createdBy: {
                        userId: c.createdByUserId,
                        name: c.createdByName
                    },
                    createdAt: c.createdAt ? new Date(Number(c.createdAt.seconds) * 1000).toISOString() : '',
                    messageCount: c.messageCount,
                    isOwner: c.isOwner,
                    forkedFrom: c.forkedFromChatId ? {
                        chatId: c.forkedFromChatId,
                        title: c.forkedFromTitle
                    } : undefined
                })),
            count: response.count
        };
    },
    async getProjectChat (projectId, chatId) {
        const response = await projectDataClient.getProjectChat({
            projectId,
            chatId
        });
        const c = response.chat;
        return {
            chat: {
                chatId: c?.chatId ?? '',
                projectId: c?.projectId ?? '',
                title: c?.title ?? '',
                createdBy: {
                    userId: c?.createdByUserId ?? '',
                    name: c?.createdByName ?? ''
                },
                createdAt: c?.createdAt ? new Date(Number(c.createdAt.seconds) * 1000).toISOString() : '',
                messageCount: c?.messageCount ?? 0,
                isOwner: c?.isOwner ?? false,
                canEdit: c?.canEdit ?? false,
                forkedFrom: c?.forkedFromChatId ? {
                    chatId: c.forkedFromChatId,
                    title: c.forkedFromTitle
                } : undefined
            }
        };
    },
    async createProjectChat (projectId, request) {
        const response = await projectDataClient.createProjectChat({
            projectId,
            title: request.title ?? ''
        });
        const c = response.chat;
        return {
            chat: {
                chatId: c?.chatId ?? '',
                projectId: c?.projectId ?? '',
                title: c?.title ?? '',
                createdBy: {
                    userId: c?.createdByUserId ?? '',
                    name: c?.createdByName ?? ''
                },
                createdAt: c?.createdAt ? new Date(Number(c.createdAt.seconds) * 1000).toISOString() : ''
            }
        };
    },
    async forkProjectChat (projectId, chatId, request) {
        const response = await projectDataClient.forkProjectChat({
            projectId,
            chatId,
            title: request.title ?? ''
        });
        const c = response.chat;
        return {
            chat: {
                chatId: c?.chatId ?? '',
                projectId: c?.projectId ?? '',
                title: c?.title ?? '',
                createdBy: {
                    userId: c?.createdByUserId ?? '',
                    name: c?.createdByName ?? ''
                },
                createdAt: c?.createdAt ? new Date(Number(c.createdAt.seconds) * 1000).toISOString() : '',
                messageCount: c?.messageCount ?? 0,
                forkedFrom: {
                    chatId: c?.forkedFromChatId ?? '',
                    title: c?.forkedFromTitle ?? ''
                }
            }
        };
    },
    async uploadProjectFiles (projectId, files) {
        // Legacy byte-upload: bytes go through Connect to zou. Kept for callers
        // that haven't migrated to the tesoro flow yet (the React hook
        // useUploadProjectFiles now uses attachArtifactsToProject below).
        const fileUploads = await Promise.all(files.map(async (file)=>{
            const buffer = await file.arrayBuffer();
            return {
                filename: file.name,
                contentType: file.type || 'application/octet-stream',
                content: new Uint8Array(buffer)
            };
        }));
        const response = await projectDataClient.uploadProjectFiles({
            projectId,
            files: fileUploads
        });
        return {
            files: (response.files ?? []).map((f)=>({
                    attachmentId: f.fileId,
                    name: f.filename,
                    contentType: f.mimeType,
                    size: Number(f.fileSize),
                    uploadedBy: {
                        userId: f.uploadedBy,
                        name: '',
                        email: ''
                    },
                    uploadedAt: timestampToString(f.uploadedAt),
                    processingStatus: toFileProcessingStatus(f.status)
                })),
            failed: (response.failed ?? []).map((f)=>({
                    name: f.filename,
                    error: f.error
                }))
        };
    },
    // Tesoro-mode: artifacts have already been uploaded via tesoro presigned
    // PUT. This call asks zou to register them as project files (creates an
    // Attachment row keyed by artifact_id, links the artifact to the project
    // on pangaea). Returns the same shape as `uploadProjectFiles` so the
    // React hook can swap implementations transparently.
    async attachArtifactsToProject (projectId, artifactIds) {
        const response = await projectDataClient.uploadProjectFiles({
            projectId,
            artifactIds
        });
        return {
            files: (response.files ?? []).map((f)=>({
                    attachmentId: f.fileId,
                    name: f.filename,
                    contentType: f.mimeType,
                    size: Number(f.fileSize),
                    uploadedBy: {
                        userId: f.uploadedBy,
                        name: '',
                        email: ''
                    },
                    uploadedAt: timestampToString(f.uploadedAt),
                    processingStatus: toFileProcessingStatus(f.status)
                })),
            failed: (response.failed ?? []).map((f)=>({
                    name: f.filename,
                    error: f.error
                }))
        };
    }
};
